import React, {FC, useState, useEffect} from "react"
import {withRouter} from "react-router-dom"
import PropTypes from "prop-types"

import {connect} from "@irati/store"
import {Email} from "@irati-web/fields/user"
import {H2, H3} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import {ReduxState} from "@irati/store/types"
import History from "@irati/utils/history"
import Button from "@irati/ui/src/Button"
import ErrorText from "@irati/ui/src/ErrorText"
import authentication from "@irati/auth"
import ssoErrorHandler from "@irati/sso/services/errors/ssoErrorHandler"
import {getAuthURL} from "@irati/sso/services/sso"
import {initiateSSOLogin} from "@irati/sso/helpers/handleSSOFlow"
import loginService from "@irati/auth/services/login"
import {ValidationButton} from "@irati/ui/src/FormItem"
import tracking from "@irati-tracking/platform/platform"

import {BusinessLogo} from "./styled"

interface Props extends WithBranding {
  load: Promise<any> | null
  isMobile: boolean
  error: string
  onClick: (email: string) => void
}

interface Context {
  apps: {[key: string]: string}
}

const EnterEmail: FC<Props> = ({error, load, branding, isMobile, onClick}, {apps}: Context) => {
  const [email, setEmail] = useState("")
  const [ssoURL, setSsoURL] = useState<string | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)

  const {formatMessage} = useIntl()
  const {showPasswordLogin, loginPageSsoButtonText} = authentication.unAuthedBusiness()

  useEffect(() => {
    tracking.enterEmail()
    getAuthURL(authentication.unAuthedBusiness().uuid!, undefined, {})
      .then(({url}) => {
        setSsoURL(url!)
      })
      .catch((err) => {
        setSsoError(ssoErrorHandler(err))
      })
  }, [])

  const singleSignOn = () => {
    setSsoError(null)

    const ssoSubmit: Promise<any> = initiateSSOLogin(ssoURL!, {useRedirect: false})
      .then((response) => {
        const {accessToken} = response
        return loginService.ssoLogin(accessToken).then(() => {
          const isAdmin = !!authentication.apps().admin

          if (isAdmin && !isMobile) {
            return History.replace({pathname: "/"}, apps.ADMIN)
          } else {
            History.replace({pathname: "/"})
          }
        })
      })
      .catch((err) => {
        setSsoError(ssoErrorHandler(err))
      })
    setSubmit(ssoSubmit)
  }
  const showEmail = !ssoURL || (ssoURL && showPasswordLogin)

  const handleOnInputChange = (value) => {
    setEmailError("")
    setEmail(value)
  }
  useEffect(() => {
    setEmailError(error)
  }, [error])

  return (
    <Flex direction="column" alignItems="center">
      {branding?.businessLogoUrl && <BusinessLogo isMobile={isMobile} src={formatMessage({id: branding?.businessLogoUrl})} />}
      {isMobile ? (
        <H3 marginBottom={2}>
          <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
        </H3>
      ) : (
        <H2 marginBottom={2}>
          <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
        </H2>
      )}

      {showEmail && (
        <>
          <Email
            label={formatMessage({id: "platform.defaultLogin.fields.email.placeholder"})}
            placeholder={formatMessage({id: "platform.defaultLogin.fields.emailValidation.enterEmail"})}
            value={email}
            onChange={handleOnInputChange}
          />
          <Flex justifyContent={isMobile ? "center " : "flex-end"} width="100%" padding={[0, 2]}>
            <ValidationButton load={load} full={isMobile} primary onClick={() => onClick(email)}>
              <FormattedMessage id="platform.oneTimePassword.enterEmail.mobileBtn" />
            </ValidationButton>
          </Flex>
          {emailError && (
            <Flex marginTop justifyContent={isMobile ? "center " : "flex-end"} width="100%">
              <ErrorText>{emailError}</ErrorText>
            </Flex>
          )}
        </>
      )}
      {ssoURL && (
        <Flex justifyContent="flex-end" width="100%" padding={[0, 2]}>
          <Button marginTop={2} primary full={isMobile} load={submit} onClick={singleSignOn}>
            {loginPageSsoButtonText || <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn" />}
          </Button>
          {ssoError && (
            <Spacing marginTop={2}>
              <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
            </Spacing>
          )}
        </Flex>
      )}
    </Flex>
  )
}

const mapStateToProps = ({mq}: ReduxState) => {
  return {
    isMobile: mq.lessThan.medium
  }
}

EnterEmail.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default withRouter(connect(mapStateToProps)(withBranding(EnterEmail)))
